import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CardContent, Grid, TextField, Button, Typography, CircularProgress, Switch, FormControlLabel, Checkbox, FormGroup, Chip, OutlinedInput, Select, MenuItem, InputLabel, FormControl, ListItemText } from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { GROUP_NAME_PREFIX, groupDisplayStem } from '../../utils/rbacDisplay';

const GroupForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true, role_ids: [] });

  useEffect(() => { fetchRoles(); if (isEdit) fetchGroup(); }, [id]);

  const fetchRoles = async () => { try { const r = await api.get('/roles?limit=100'); setRoles(r.data.data); } catch (e) {} };

  const fetchGroup = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/groups/${id}`);
      const group = r.data.data;
      const groupRoles = group.roles || group.Roles || [];
      setFormData({
        ...group,
        name: groupDisplayStem(group.name),
        role_ids: groupRoles.map((gr) => gr.id),
      });
    } catch (e) { toast.error('Failed to fetch'); navigate('/admin/groups'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        is_active: formData.is_active,
        role_ids: formData.role_ids,
      };
      if (isEdit) await api.put(`/groups/${id}`, payload);
      else await api.post('/groups', payload);
      toast.success(`Group ${isEdit ? 'updated' : 'created'}`);
      navigate('/admin/groups');
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/groups')}>Back</Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{isEdit ? 'Edit Group' : 'New Group'}</Typography>
      </Box>
      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Group Information</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Group name (short)"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  helperText={`Stored as ${GROUP_NAME_PREFIX}{name}`}
                />
              </Grid>
              <Grid item xs={12} md={6}><FormControlLabel control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />} label="Active" /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Description" name="description" value={formData.description || ''} onChange={handleChange} multiline rows={2} /></Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Assign Roles</InputLabel>
                  <Select multiple name="role_ids" value={formData.role_ids} onChange={handleChange} input={<OutlinedInput label="Assign Roles" />}
                    renderValue={(selected) => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{selected.map(id => { const r = roles.find(role => role.id === id); return r ? <Chip key={id} label={r.name} size="small" /> : null; })}</Box>)}>
                    {roles.map(r => (<MenuItem key={r.id} value={r.id}><Checkbox checked={formData.role_ids.includes(r.id)} /><ListItemText primary={r.name} secondary={r.description} /></MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/admin/groups')}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />} disabled={saving} sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>{saving ? 'Saving...' : 'Save Group'}</Button>
        </Box>
      </form>
    </Box>
  );
};

export default GroupForm;
